import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useApi, errorText } from "@/lib/api";
import { parsePasted } from "@/lib/parsePaste";
import { formatMoney } from "@/lib/format";

const Status = ({ r }) => {
  if (r.errors.length) return <span className="text-rose-400">{r.errors.join("; ")}</span>;
  if (r.warnings.length) return <span className="text-amber-300">{r.warnings.join("; ")}</span>;
  return <span className="text-emerald-400">Ready</span>;
};

export default function PasteDialog({ open, onOpenChange, text, setText, fy, categories, onImported }) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const rows = useMemo(() => parsePasted(text, categories, fy), [text, categories, fy]);
  const ready = rows.filter((r) => !r.errors.length);
  const blocked = rows.length - ready.length;

  const importRows = async () => {
    setBusy(true);
    try {
      const body = ready.map((r) => ({ date: r.date, description: r.description, received: r.received, spent: r.spent,
        category: r.category, notes: r.notes, for_whom: r.for_whom, what: r.what, project: r.project, fy: fy.label }));
      const res = await api("post", "/transactions/bulk", { rows: body });
      toast.success(`Imported ${res.inserted} rows into ${fy.label}${blocked ? ` · ${blocked} skipped` : ""}`);
      setText("");
      onImported();
      onOpenChange(false);
    } catch (e) { toast.error(errorText(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card max-w-5xl" data-testid="paste-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Paste rows into {fy?.label}</DialogTitle>
          <DialogDescription>
            Copy rows from Excel or a bank CSV with columns: Date, Description, Received, Spent, Category, Notes.
          </DialogDescription>
        </DialogHeader>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} className="font-mono text-xs"
          placeholder={"1/07/2025\tClient fee\t4500.00\t\tRes - Arch\t"} data-testid="paste-textarea" />
        {rows.length > 0 && (
          <div className="max-h-72 overflow-auto border border-border">
            <table className="w-full text-xs" data-testid="paste-preview-table">
              <thead className="sticky top-0 bg-card">
                <tr className="text-left text-muted-foreground uppercase tracking-wider border-b border-border">
                  {["Date", "Description", "Received", "Spent", "Category", "Status"].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className={`border-b border-border ${r.errors.length ? "bg-rose-500/10" : ""}`} data-testid="paste-preview-row">
                    <td className="px-3 py-1.5 font-mono">{r.date || r.rawDate}</td>
                    <td className="px-3 py-1.5">{r.description}</td>
                    <td className="px-3 py-1.5 font-mono text-right">{formatMoney(r.received)}</td>
                    <td className="px-3 py-1.5 font-mono text-right">{formatMoney(r.spent)}</td>
                    <td className="px-3 py-1.5">{r.categoryName || <span className="text-amber-300">Blank</span>}</td>
                    <td className="px-3 py-1.5"><Status r={r} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter className="items-center gap-3">
          <span className="text-xs text-muted-foreground mr-auto" data-testid="paste-summary">
            {ready.length} ready{blocked ? ` · ${blocked} blocked (won't import)` : ""}
          </span>
          <Button onClick={importRows} disabled={!ready.length || busy} data-testid="paste-import-button">
            {busy ? "Importing…" : `Import ${ready.length} rows`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
